import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CarIcon from '@mui/icons-material/DirectionsCar';
import ExitIcon from '@mui/icons-material/ExitToApp';
import OfferIcon from '@mui/icons-material/LocalOffer';
import NotifyIcon from '@mui/icons-material/NotificationsActive';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MoneyIcon from '@mui/icons-material/Money';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { calcEst, showEst, optionsRequired } from '../common/sharedFunctions';
import SosIcon from '@mui/icons-material/Sos';
import { FONT_FAMILY } from '../common/sharedFunctions';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import StoreIcon from '@mui/icons-material/Store';
import ExploreIcon from '@mui/icons-material/Explore';
import LanguageIcon from '@mui/icons-material/Language';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import logoWhite from '../assets/img/logo138x75white.png';

const DRAWER_WIDTH = 240;
const DARK_BG = '#0F1117';
const PURPLE = '#8E4585';
const GREEN  = '#46F34D';
const MUTED = '#9AA0B2';

export default function ResponsiveDrawer(props) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('ridder-lang');
    return saved === 'en' ? 'en' : 'es';
  });

  const toggleLang = () => {
    setLang((prev) => {
      const next = prev === 'es' ? 'en' : 'es';
      localStorage.setItem('ridder-lang', next);
      i18n.changeLanguage(next);
      return next;
    });
  };
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { signOff } = api;
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const businessdata = useSelector(state => state.businessdata || {});
  const companies = businessdata.companies || [];
  const dispatch = useDispatch();

  const pendingCount = companies.filter(c => c.status === 'pending').length;

  const LogOut = () => {
    dispatch(signOff());
  };

  const [role, setRole] = useState(null);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleDrawer = () => {
    setMobileOpen(false);
  };

  const { pathname: currentPath } = useLocation();

  const menuItems = [
    { name: t('home'), url: '/', icon: <HomeIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
    { name: t('dashboard_text'), url: '/dashboard', icon: <DashboardIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin'] },
    { name: t('booking_history'), url: '/bookings', icon: <ViewListIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
    { name: t('addbookinglable'), url: '/addbookings', icon: <ContactPhoneIcon sx={{ fontSize: 20 }} />, access: calcEst && !showEst && !optionsRequired ? ['customer'] : ['admin', 'fleetadmin', 'customer'] },
    { name: t('user'), url: '/users/0', icon: <EmojiPeopleIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin'] },
    { name: t('car_type'), url: '/cartypes', icon: <CarIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: t('cars'), url: '/cars', icon: <CarIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin', 'driver'] },
    { name: t('withdraws_web'), url: '/withdraws', icon: <MoneyIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: t('add_to_wallet'), url: '/addtowallet', icon: <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: 'Transferencias', url: '/wallettransfers', icon: <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: t('report'), url: '/allreports', icon: <AssessmentIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin'] },
    { name: t('promo'), url: '/promos', icon: <OfferIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: t('push_notifications'), url: '/notifications', icon: <NotifyIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: t('sos'), url: '/sos', icon: <SosIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: t('complain'), url: '/complain', icon: <AnnouncementOutlinedIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: 'Soporte', url: '/support', icon: <HeadsetMicIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: 'Empresas', url: '/companies', icon: <StoreIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin'], badge: pendingCount },
    { name: 'Lugares Descubre', url: '/lugares', icon: <ExploreIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin'] },
    { name: 'IA', url: '/ia', icon: <SmartToyIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: 'Billetera y Recargas', url: '/userwallet', icon: <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />, access: ['driver', 'customer', 'fleetadmin'] },
    { name: 'Descubre', url: '/descubre', icon: <ExploreIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin'] },
    { name: t('settings_title'), url: '/settings', icon: <PhoneIphoneIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: t('payment_settings'), url: '/paymentsettings', icon: <PhoneIphoneIcon sx={{ fontSize: 20 }} />, access: ['admin'] },
    { name: t('profile'), url: '/profile', icon: <AccountCircleIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
    { name: t('logout'), url: 'logout', icon: <ExitIcon sx={{ fontSize: 20 }} />, access: ['admin', 'fleetadmin', 'driver', 'customer'] },
  ];

  const profile = auth.profile || {};
  const displayName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'Usuario';
  const roleLabel = (profile.usertype === 'admin' || profile.usertype === 'fleetadmin') ? 'Admin · Ridder' : profile.usertype || '';
  const avatarLetter = (profile.firstName || displayName || 'U').charAt(0).toUpperCase();

  const drawer = (
    <Box sx={{ backgroundColor: DARK_BG, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '18px 16px 14px', flexShrink: 0, borderBottom: '1px solid #ffffff0e' }}>
        <img src={logoWhite} alt="Ridder" style={{ height: 52, width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 0 8px #46F34D40)' }} />
        <Box sx={{ width: '1px', height: 28, backgroundColor: '#ffffff12', mx: 0.5 }} />
        <Typography sx={{ color: MUTED, fontSize: 10, letterSpacing: 2, fontFamily: FONT_FAMILY, fontWeight: 700 }}>
          ADMIN
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Box onClick={toggleLang} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: '7px', border: `1px solid #ffffff18`, backgroundColor: '#ffffff08', cursor: 'pointer', transition: 'background 0.2s', '&:hover': { backgroundColor: '#ffffff14' } }}>
            <LanguageIcon sx={{ fontSize: 12, color: GREEN }} />
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 10, fontWeight: 700, color: GREEN }}>{lang.toUpperCase()}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Nav items - scrollable */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: '8px', py: '4px' }}>
        {role ? (
          <List disablePadding>
            {menuItems.map((item, index) => {
              if (!item.access.includes(role)) return null;
              const isActive = item.url !== 'logout' && currentPath === item.url;
              const isLogout = item.url === 'logout';

              const iconNode = (item.badge > 0)
                ? (
                  <Badge
                    badgeContent={item.badge}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: PURPLE,
                        color: '#fff',
                        fontSize: 10,
                        minWidth: 16,
                        height: 16,
                        padding: '0 4px',
                      }
                    }}
                  >
                    {item.icon}
                  </Badge>
                )
                : item.icon;

              const itemContent = (
                <Box
                  key={'nav-' + index}
                  onClick={() => { handleDrawer(); if (isLogout) LogOut(); }}
                  component={isLogout ? 'div' : Link}
                  to={isLogout ? undefined : item.url}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    borderRadius: '10px',
                    paddingY: '10px',
                    paddingX: '12px',
                    marginBottom: '4px',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    backgroundColor: isActive ? `${GREEN}18` : 'transparent',
                    color: isActive ? '#fff' : MUTED,
                    transition: 'background-color 0.15s, color 0.15s',
                    borderLeft: isActive ? `3px solid ${GREEN}` : '3px solid transparent',
                    '&:hover': {
                      backgroundColor: '#ffffff0e',
                      color: '#fff',
                    },
                    '& svg': {
                      color: isActive ? GREEN : 'inherit',
                    },
                  }}
                >
                  {iconNode}
                  <Typography sx={{
                    fontSize: 14,
                    fontFamily: FONT_FAMILY,
                    fontWeight: 500,
                    color: 'inherit',
                    textAlign: isRTL === 'rtl' ? 'right' : 'left',
                  }}>
                    {item.name}
                  </Typography>
                </Box>
              );

              return itemContent;
            })}
          </List>
        ) : null}
      </Box>

      {/* Bottom user section */}
      <Box sx={{
        flexShrink: 0,
        padding: '16px',
        borderTop: '1px solid #ffffff12',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}>
        <Avatar
          src={profile.photo || undefined}
          sx={{
            width: 36, height: 36,
            bgcolor: PURPLE,
            fontSize: 14,
            fontFamily: FONT_FAMILY,
            flexShrink: 0,
          }}
        >
          {avatarLetter}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: FONT_FAMILY, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName}
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 11, fontFamily: FONT_FAMILY, lineHeight: 1 }}>
            {roleLabel}
          </Typography>
        </Box>
        <IconButton onClick={LogOut} sx={{ color: MUTED, padding: '4px', '&:hover': { color: '#fff', backgroundColor: '#ffffff12' } }}>
          <ExitIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex', direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <CssBaseline />
      <style>
        {`
          ::-webkit-scrollbar {
            width: 4px;
          }
          ::-webkit-scrollbar-thumb {
            background-color: #ffffff20;
            border-radius: 6px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background-color: ${PURPLE};
          }
          ::-webkit-scrollbar-track {
            background-color: transparent;
          }
        `}
      </style>
      <AppBar
        position="fixed"
        style={isRTL === 'rtl'
          ? { marginRight: DRAWER_WIDTH, backgroundColor: DARK_BG }
          : { marginLeft: DRAWER_WIDTH, backgroundColor: DARK_BG }}
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
          boxShadow: 'none',
          borderBottom: '1px solid #ffffff12',
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: '#fff' }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
        aria-label="sidebar navigation"
      >
        <Drawer
          container={container}
          variant="temporary"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              backgroundColor: DARK_BG,
              border: 'none',
            },
            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              backgroundColor: DARK_BG,
              border: 'none',
            },
            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` }, backgroundColor: DARK_BG, minHeight: '100vh' }}
      >
        <Toolbar />
        {props.children}
      </Box>
    </Box>
  );
}
